import {useCallback, useEffect, useRef, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {apiClient} from '@common/http/query-client';
import {useHandleLoginSuccess} from '@common/auth/requests/use-login';
import {useAuth} from '@common/auth/use-auth';
import {useBootstrapData} from '@common/core/bootstrap-data/bootstrap-data-context';

/**
 * If CGMSISDN is present in query params, attempt to log in or register
 * the user by that phone automatically.
 */
export function useXceedAutoLogin() {
  const [params] = useSearchParams();
  const {data} = useBootstrapData();
  const conversionUrlRef = useRef<string | undefined>(undefined);
  const hasBootstrapDataRef = useRef(false);
  const conversionSentKeysRef = useRef<Set<string>>(new Set());
  const conversionRetryScheduledRef = useRef<Set<string>>(new Set());
  const handleSuccess = useHandleLoginSuccess();
  const {user} = useAuth();
  const initiatedRef = useRef(false);
  const dataLayerEventSentRef = useRef(false);
  const [loading, setLoading] = useState(false);
  conversionUrlRef.current = data?.environment?.CONVERSION_URL;
  hasBootstrapDataRef.current = Boolean(data?.environment);
  const sendSubscribeSuccess = useCallback(
    (status: string, msisdn: string) => {
      const conversionKey = `${status}:${msisdn}:${params.toString()}`;
      const eventProperties = {
        event: 'subscribe_success',
        status,
        msisdn,
        payment_method: 'xceed_MTN',
      };

      if (typeof window !== 'undefined') {
        if (!dataLayerEventSentRef.current) {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer?.push(eventProperties);
          dataLayerEventSentRef.current = true;
        }
        const conversionUrl = conversionUrlRef.current;
        if (conversionUrl) {
          if (conversionSentKeysRef.current.has(conversionKey)) {
            return;
          }
          conversionSentKeysRef.current.add(conversionKey);
          const allParams = Object.fromEntries(params.entries());
          fetch(conversionUrl, {
            method: 'POST',
            body: JSON.stringify({
              ...eventProperties,
              gclick: params.get('gclick'),
              params: allParams,
            }),
            headers: {
              'Content-Type': 'application/json',
            },
          });
        } else if (!hasBootstrapDataRef.current) {
          // wait until bootstrap data is available before skipping the conversion request
          if (!conversionRetryScheduledRef.current.has(conversionKey)) {
            conversionRetryScheduledRef.current.add(conversionKey);
            setTimeout(() => {
              conversionRetryScheduledRef.current.delete(conversionKey);
              sendSubscribeSuccess(status, msisdn);
            }, 300);
          }
        }
      } else {
        // retry shortly
        setTimeout(() => sendSubscribeSuccess(status, msisdn), 300);
      }
    },
    [params],
  );

  useEffect(() => {
    const rawMsisdn = params.get('CGMSISDN');
    const msisdn = rawMsisdn ? `+${rawMsisdn}` : null;

    const status = params.get('CGSTATUS');
    const trackingParameters = Object.fromEntries(params.entries());

    if (status === '0') {
      sendSubscribeSuccess(status, msisdn ?? '');
    }

    if (!msisdn || initiatedRef.current) return;

    initiatedRef.current = true;
    setLoading(true);

    const baseURL = window.location.host.replace(/(^\w+:|^)\/\//, '').trim();
    const loginPayload = {
      phone: msisdn,
      password: msisdn,
      email: `${msisdn}@${baseURL}`,
      remember: true,
      token_name: 'browser',
      baseURL,
      tracking_parameters:
        Object.keys(trackingParameters).length > 0
          ? trackingParameters
          : undefined,
    };

    (async () => {
      try {
        // if logged in as a different user, log out first
        if (user && user.phone !== msisdn) {
          try {
            await apiClient.post('auth/logout');
          } catch (e) {
            // proceed regardless; auto-login will refresh bootstrap data
          }
        } else if (user && user.phone === msisdn) {
          return;
        }

        const response = await apiClient.post('auth/login', loginPayload);
        handleSuccess(response.data);
      } catch (error: any) {
        if (error.response?.status === 422) {
          // user does not exist, register and then consider them logged in
          const registerResponse = await apiClient.post('auth/register', {
            phone: msisdn,
            tracking_parameters:
              Object.keys(trackingParameters).length > 0
                ? trackingParameters
                : undefined,
          });
          handleSuccess(registerResponse.data);
        } else {
          initiatedRef.current = false;
          throw error;
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [params, user, handleSuccess, sendSubscribeSuccess]);

  return loading;
}
